import { Link, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, Copy, ExternalLink, Loader2, Phone } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ageFromDob, formatDateOnly, toDateOnlyString } from "@/lib/datetime"
import { tcmErrorMessage } from "../../api"
import { useTcmCarePlan, useTcmEpisode, useTcmNote } from "../../hooks"
import { levelLabel, urgencyVariant } from "../../lib/board"
import { isOpenBridge, journey, NEED_LABELS } from "../../lib/careplan"
import type { TcmCarePlan, TcmEpisodeDetail } from "../../types"
import { TcmCaseSections } from "../TcmCaseDrawer"
import { useCopyTcmNote } from "../useCopyTcmNote"
import { useTcmView } from "../useTcmView"
import { CallsPanel } from "./CallsPanel"
import { JourneyStepper } from "./JourneyStepper"
import { NeedsPanel } from "./NeedsPanel"
import { PatientPlanPanel } from "./PatientPlanPanel"
import { RiskChip, RiskPanel } from "./RiskPanel"

const TABS = [
  { id: "case", label: "Case" },
  { id: "needs", label: "Needs at home" },
  { id: "plan", label: "Patient plan" },
  { id: "risk", label: "Readmission risk" },
  { id: "calls", label: "Weekly calls" },
] as const
type TabId = (typeof TABS)[number]["id"]

const TRIGGER =
  "h-10 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 md:px-4 text-sm whitespace-nowrap"

const md = (d: string | null | undefined) => formatDateOnly(d ?? null, "EEE M/d")

/**
 * One case's whole 30 days on a page: who and what stay, the journey from discharge to day 30, the
 * working sections the drawer has (next step, clock, note, billing), and the care plan: needs at
 * home, the patient's printed plan, the readmission risk and Pear's weekly calls.
 */
export function TcmCasePage() {
  const { episodeId = "" } = useParams<{ episodeId: string }>()
  const [params, setParams] = useSearchParams()
  const tab = (TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "case") as TabId
  const { summary, ehrFor } = useTcmView()
  const today = summary.data?.today ?? toDateOnlyString()
  const { data: ep, isLoading, error } = useTcmEpisode(episodeId)
  const { data: note } = useTcmNote(episodeId, !!ep?.note)
  const carePlan = useTcmCarePlan(episodeId)
  const { copy, copyingId } = useCopyTcmNote()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (!ep) {
    return (
      <div className="mx-auto max-w-xl space-y-3 px-4 py-24 text-center">
        <p className="text-sm text-muted-foreground">
          {error ? `Couldn't open this case: ${tcmErrorMessage(error)}` : "Case not found."}
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/tcm">Back to Transitional Care</Link>
        </Button>
      </div>
    )
  }

  const ehr = ehrFor(ep.organization_id)
  const plan = carePlan.data
  return (
    <div className="min-h-full bg-background">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-4 md:px-6 md:py-6">
        <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 gap-1.5 text-xs text-muted-foreground">
          <Link to="/tcm">
            <ArrowLeft className="h-3.5 w-3.5" />
            Transitional Care
          </Link>
        </Button>

        <CaseHeader
          ep={ep}
          plan={plan}
          ehr={ehr}
          onCopy={() => copy(ep.id, ehr)}
          copying={copyingId === ep.id}
        />

        <Card>
          <CardContent className="px-4 py-4 md:px-6">
            <JourneyStepper steps={journey(ep, plan, today)} />
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={(v) => setParams((p) => { p.set("tab", v); return p }, { replace: true })}>
          <div className="border-b overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList className="h-10 w-max gap-0 border-0 bg-transparent p-0">
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className={TRIGGER}>
                  {t.label}
                  {t.id === "needs" && plan && plan.bridges.some(isOpenBridge) && (
                    <span className="ml-1.5 rounded-full bg-warning/15 px-1.5 text-[11px] text-warning">
                      {plan.bridges.filter(isOpenBridge).length}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="case" className="mt-4">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
              <TcmCaseSections
                ep={ep}
                today={today}
                ehr={ehr}
                noteText={note?.text}
                onCopy={() => copy(ep.id, ehr)}
                copying={copyingId === ep.id}
              />
              <CarePlanSummary plan={plan} loading={carePlan.isLoading} onTab={(t) => setParams({ tab: t }, { replace: true })} />
            </div>
          </TabsContent>
          <TabsContent value="needs" className="mt-4">
            <PlanOr plan={plan} loading={carePlan.isLoading}>
              {(p) => <NeedsPanel episodeId={ep.id} organizationId={ep.organization_id} bridges={p.bridges} />}
            </PlanOr>
          </TabsContent>
          <TabsContent value="plan" className="mt-4">
            <PlanOr plan={plan} loading={carePlan.isLoading}>
              {(p) => <PatientPlanPanel ep={ep} plan={p} today={today} />}
            </PlanOr>
          </TabsContent>
          <TabsContent value="risk" className="mt-4">
            <PlanOr plan={plan} loading={carePlan.isLoading}>{(p) => <RiskPanel risk={p.risk} />}</PlanOr>
          </TabsContent>
          <TabsContent value="calls" className="mt-4">
            <PlanOr plan={plan} loading={carePlan.isLoading}>{(p) => <CallsPanel plan={p} />}</PlanOr>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

function CaseHeader({
  ep,
  plan,
  ehr,
  onCopy,
  copying,
}: {
  ep: TcmEpisodeDetail
  plan: TcmCarePlan | null | undefined
  ehr: string
  onCopy: () => void
  copying: boolean
}) {
  const age = ageFromDob(ep.patient_dob)
  const isTcm = ep.kind === "TCM"
  const home = ep.post_acute_discharge_date ?? ep.discharge_date
  const los = plan?.risk.los_days
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 px-4 py-4 md:flex-row md:items-start md:justify-between md:px-6">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{ep.patient_name}</h1>
            <StatusBadge variant={isTcm ? "success" : ep.kind === "UNKNOWN" ? "warning" : "info"}>
              {isTcm ? "TCM" : ep.kind === "UNKNOWN" ? "Records needed" : "ED follow-up"}
            </StatusBadge>
            {plan && <RiskChip tier={plan.risk.tier} complete={plan.risk.tier_complete} />}
          </div>
          <p className="text-xs text-muted-foreground">
            {[ep.mrn && `MRN ${ep.mrn}`, ep.patient_dob && `DOB ${formatDateOnly(ep.patient_dob, "M/d/yyyy")}`, age !== null && `${age} y`,
              ep.pcp && `PCP ${ep.pcp}`, ep.insurance]
              .filter(Boolean)
              .join(" · ")}
          </p>
          {ep.patient_phone && (
            <a href={`tel:${ep.patient_phone}`} className="inline-flex items-center gap-1 text-xs hover:underline">
              <Phone className="h-3 w-3" />
              {ep.patient_phone}
            </a>
          )}
          <p className="text-[13px]">
            {levelLabel(ep.level_of_care)}
            {ep.facility ? ` · ${ep.facility}` : ""}
            {ep.admit_date ? ` · admitted ${md(ep.admit_date)}` : ""}
            {home ? ` · ${ep.post_acute_discharge_date ? "home from rehab" : "discharged"} ${md(home)}` : ""}
            {los != null ? ` · ${los} day${los === 1 ? "" : "s"}` : ""}
          </p>
          <StatusBadge variant={urgencyVariant(ep.stage.urgency)} dot>{ep.stage.next_action}</StatusBadge>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {ep.note && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onCopy} disabled={copying}>
              {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
              Copy note for {ehr}
            </Button>
          )}
          <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 text-xs">
            <a href={`/patients/${ep.patient_id}`} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              Patient page
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function CarePlanSummary({
  plan,
  loading,
  onTab,
}: {
  plan: TcmCarePlan | null | undefined
  loading: boolean
  onTab: (tab: TabId) => void
}) {
  if (loading) return null
  if (!plan) {
    return (
      <Card className="self-start">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Care plan</CardTitle>
        </CardHeader>
        <CardContent className="text-[13px] text-muted-foreground">
          Pear builds it once the hospital records are read: the readmission risk, what's needed at home, the patient's
          4-week plan, and the weekly calls.
        </CardContent>
      </Card>
    )
  }
  const open = plan.bridges.filter(isOpenBridge)
  const next = plan.checkins.find((c) => ["PENDING", "SCHEDULED", "ATTEMPTED"].includes(c.status))
  return (
    <Card className="self-start">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Care plan</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-[13px]">
        <button type="button" className="block text-left" onClick={() => onTab("risk")}>
          <RiskChip tier={plan.risk.tier} complete={plan.risk.tier_complete} />
        </button>
        <div>
          <button type="button" className="font-medium hover:underline" onClick={() => onTab("needs")}>
            {open.length ? `${open.length} need${open.length === 1 ? "" : "s"} to arrange` : "Needs at home in place"}
          </button>
          {open.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-muted-foreground">
              {open.slice(0, 4).map((b) => (
                <li key={b.id}>{NEED_LABELS[b.need_key]}{b.detail ? `: ${b.detail}` : ""}</li>
              ))}
            </ul>
          )}
        </div>
        <button type="button" className="block text-left hover:underline" onClick={() => onTab("plan")}>
          Patient's 4-week plan{plan.languages.length ? "" : " (in review)"}
        </button>
        <button type="button" className="block text-left text-muted-foreground hover:underline" onClick={() => onTab("calls")}>
          {next ? `Next call: week ${next.week}, ${md(next.window_start)}–${md(next.window_end)}` : "Weekly calls not scheduled"}
        </button>
      </CardContent>
    </Card>
  )
}

function PlanOr({
  plan,
  loading,
  children,
}: {
  plan: TcmCarePlan | null | undefined
  loading: boolean
  children: (plan: TcmCarePlan) => React.ReactNode
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (!plan) {
    return (
      <div className="rounded-xl border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        No care plan for this case yet. Pear builds it once the hospital records are read.
      </div>
    )
  }
  return <>{children(plan)}</>
}
